import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useMutation } from '@tanstack/react-query';
import { Button, SelectField, Status, TextField } from '@components';
import { ExternalIdentityService } from '@data';
import { LinkWithReferencesPayload } from '@types';
import { isEmpty } from 'lodash';

import styles from './clientRemove.module.scss';

interface ClientRemoveProps {
	clientId: string;
	onClickBack?: () => void;
}

export const ClientRemove = ({
	clientId,
	onClickBack = () => {
		return;
	},
}: ClientRemoveProps) => {
	// State
	const [removeClientOption, setRemoveClientOption] = useState<string>('');
	const [showRemoveClientOptionError, setShowRemoveClientOptionError] = useState(false);
	const [showRemoveClientSuccess, setShowRemoveClientSuccess] = useState(false);
	const [showRemoveClientError, setShowRemoveClientError] = useState(false);
	const [removeClientError, setRemoveClientError] = useState('');
	const [showRemoveClientDescError, setShowRemoveClientDescError] = useState(false);
	const [removeClientDesc, setRemoveClientDesc] = useState('');

	// Other
	const router = useRouter();

	// Data
	const unlinkContactToClient = useMutation({
		mutationFn: (unlinkRequest: LinkWithReferencesPayload) => {
			return ExternalIdentityService.linkWithReferences(unlinkRequest);
		},
	});

	// Helpers
	const validateForm = () => {
		setRemoveClientError('');
		setShowRemoveClientOptionError(false);
		setShowRemoveClientSuccess(false);
		let validated = true;
		if (isEmpty(removeClientOption)) {
			setShowRemoveClientOptionError(true);
			setRemoveClientError('Please resolve the errors above.');
			validated = false;
		}
		if (isEmpty(removeClientDesc)) {
			setShowRemoveClientDescError(true);
			setRemoveClientError('Please resolve the errors above.');
			validated = false;
		}
		return validated;
	};

	const newUnlinkRequest = (): LinkWithReferencesPayload => {
		return {
			comment: removeClientOption + ' - ' + removeClientDesc,
			clientReference: clientId,
			isUnlinkRequest: true,
		};
	};

	const onSubmitRemoveClient = async () => {
		setShowRemoveClientOptionError(false);
		setShowRemoveClientDescError(false);
		setShowRemoveClientSuccess(false);
		setShowRemoveClientError(false);
		setRemoveClientError('');
		const validated = validateForm();
		if (validated) {
			const data = newUnlinkRequest();
			await unlinkContactToClient
				.mutateAsync(data)
				.then(() => {
					setShowRemoveClientSuccess(true);
				})
				.catch(() => {
					setRemoveClientError('Server error. Please try again');
					setShowRemoveClientError(true);
				});
		}
	};

	const navigateToSubmittedRequests = () => {
		router.push('/inbox');
	};

	return (
		<>
			<p className={styles['modal-title']}>Request Client Unlink</p>
			{unlinkContactToClient.isSuccess ? (
				<div>
					<label className={styles[`dropdown-name`]}>
						<p>{'Select client unlink reason'}</p>
					</label>

					<p className={styles['text-field-disabled']}>{removeClientOption}</p>
				</div>
			) : (
				<SelectField
					label="Select client unlink reason"
					options={['Client incorrect', 'Client no longer associated to me']}
					placeholder="Select"
					errorMsg="Please select a reason"
					error={showRemoveClientOptionError}
					handleChange={v => {
						setRemoveClientOption(v);
						setShowRemoveClientOptionError(false);
					}}
					testId="clientRemoveReason"
				/>
			)}
			<TextField
				type="textarea"
				label="Please provide more details."
				placeholder="E.g. Details should be XXXX"
				disabled={showRemoveClientSuccess}
				handleChange={e => {
					setRemoveClientDesc(e.target.value);
					setShowRemoveClientDescError(false);
				}}
				status={showRemoveClientDescError ? 'error' : 'valid'}
				errorMsg="Please complete this field"
				testId="clientRemoveComment"
			/>
			{!showRemoveClientSuccess ? (
				<Button
					size="small"
					customStyles={styles['modal-button']}
					type="primary"
					onClick={onSubmitRemoveClient}
					isLoading={unlinkContactToClient.isPending}
					disabled={unlinkContactToClient.isSuccess}
					data-testid="clientRemoveSubmit"
				>
					Submit
				</Button>
			) : (
				<>
					<div className={styles['buttons-container']}>
						<Button
							size="large"
							customStyles={styles['modal-button']}
							type="secondary"
							onClick={navigateToSubmittedRequests}
							isLoading={unlinkContactToClient.isPending}
							disabled={!unlinkContactToClient.isSuccess}
							data-testid="viewSubmittedRequests"
						>
							View Submitted requests
						</Button>
						<Button
							size="large"
							customStyles={styles['modal-button']}
							type="primary"
							onClick={onClickBack}
							isLoading={unlinkContactToClient.isPending}
							disabled={!unlinkContactToClient.isSuccess}
							data-testid="backToAccountDetails"
						>
							Back to Account <p />
							details
						</Button>
					</div>
					<div className={styles['success-container']}>
						<div className={styles['drawer-content-status']}>
							<Status
								status={'success'}
								show={true}
								closable={false}
								// eslint-disable-next-line @typescript-eslint/no-empty-function
								onClose={() => {}}
								title={'Submitted'}
								msg={`Thank you for your submission. Your reference number is ${unlinkContactToClient.data?.reference}.
									  Please allow 5 business days for your request to be processed.`}
							/>
						</div>
					</div>
				</>
			)}
			<div className={styles['drawer-content-status']}>
				<Status
					status={'error'}
					show={showRemoveClientError || showRemoveClientDescError || showRemoveClientOptionError}
					onClose={() => {
						setShowRemoveClientOptionError(false);
						setShowRemoveClientDescError(false);
						setShowRemoveClientError(false);
					}}
					msg={removeClientError}
				/>
			</div>
		</>
	);
};
